import { APP_VERSION } from "./version";

export type CompanionReleaseNote = {
  version: string;
  /** Short ops line under the version number. */
  summary: string;
  bullets: string[];
};

/**
 * Phone notes for the next merge. Not a shipped version. Do not bump the companion number here.
 */
export const UNASSIGNED_RELEASE_NOTES: string[] = [];

/** Newest first. Keep 3–8 bullets per ship — not a novel. */
export const RELEASE_NOTES: CompanionReleaseNote[] = [
  {
    version: "0.1.58",
    summary: "Revenue matches the desk",
    bullets: [
      "Revenue on the phone comes from the desk's posted charter line, so it matches the company ledger.",
      "An accepted quote still counts after it leaves the live inbox.",
      "The pilot board can focus on one flight and shows its block times, fuel in whole pounds, logbook and W&B.",
      "Independent of the desk number.",
    ],
  },
  {
    version: "0.1.57",
    summary: "An empty schedule says why",
    bullets: [
      "Schedule names why it is empty: still connecting to Nanode, offline with nothing saved, no company trips, no crew link, or nothing assigned.",
      "Nothing assigned includes how many trips are on the company board.",
      "A full cache leaves the board already on screen. The next pull tries again.",
      "An older company snapshot still opens.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.56",
    summary: "The phone shows the airline name and logo",
    bullets: [
      "The header and the company lines use the airline name from the desk when one is set. Otherwise they keep the company name.",
      "A logo appears when the desk published a pack mark or a small upload. A logo kept only on the desk is not shown.",
      "An older snapshot without a logo still opens.",
    ],
  },
  {
    version: "0.1.55",
    summary: "Desk alerts and time to departure on the home screen",
    bullets: [
      "Home shows the desk fleet counts, amber and red alert tiers, and the time to departure.",
      "The countdown uses the published STD and keeps moving between pulls.",
      "An older company snapshot without that strip still loads. The phone builds the countdown from the trips it already has.",
      "Dispatch replies from 0.1.54 stay on Today and the flight brief. Independent of the desk number.",
    ],
  },
  {
    version: "0.1.54",
    summary: "Dispatch replies reach the desk on the company board",
    bullets: [
      "Today and the flight brief show dispatch messages, with an unread count and a short chime.",
      "A reply is a delay, a maintenance write-up, a fuel uplift, or free text. It posts on the company board for the desk to file.",
      "A Hoppie-capable tail stays a read-only copy and says the message was also sent to the aircraft.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.53",
    summary: "A browser device key, kept off until central sync",
    bullets: [
      "This browser can hold a non-extractable device key and show up as a Browser device.",
      "Nanode devices would list local time with a zone label, a coarse IP prefix, and rename or revoke. The card stays hidden while central sync is off.",
      "Sign-in, Pull snapshot, and the company token field are unchanged.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.52",
    summary: "Dispatch messages and a reply for delay, fuel, and maintenance",
    bullets: [
      "Messages on the pilot's trip include company ACARS text from the desk.",
      "When the tail is marked Hoppie-capable, an uplink says it was also sent to the aircraft.",
      "A reply can be a delay code with minutes and a new ETD, a maintenance write-up with an ATA chapter, a fuel uplift, or free text.",
      "The reply stays on this phone until the desk has it. Independent of the desk number.",
    ],
  },
  {
    version: "0.1.51",
    summary: "Phone errors are recorded on the device",
    bullets: [
      "A screen crash or an uncaught error is written to the on-device diagnostics log.",
      "More explains that the shared error log lives on the desk Diagnostics page.",
      "Secrets are stripped before a line is stored.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.50",
    summary: "Charcoal and amber Northstar mark",
    bullets: [
      "The home-screen icon, splash, and maskable icons use the charcoal tile with the amber fin and white star.",
      "Android themed icons use a monochrome fin, and the update notice uses the white star.",
      "The browser theme colour and the splash behind the first frame are charcoal. Day and Night inside the app are unchanged.",
      "The header shows the new star next to the product name. Independent of the desk number.",
    ],
  },
  {
    version: "0.1.49",
    summary: "Fuel figures match the desk",
    bullets: [
      "Trip, ticket, and log fuel use the same whole-number figures as the desk, with thousands separators and a unit.",
      "Pounds are the default. A tail or company unit you already chose, including kilograms, still shows that unit.",
      "An older Hawker kilogram stamp does not change the phone figures. Stored fuel quantities stay as filed.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.48",
    summary: "Schedule and theme follow the desk",
    bullets: [
      "Scheduled trips published by any desk, Main or Secondary, show after the board pull. Publishing the same trips again does not duplicate them.",
      "Day and Night use the same shared tokens as the desk, including the Ops Classic navy bar and lime accent. The phone Day/Night control still switches the panel.",
      "The header names Main, Secondary, or both when those desks have published.",
      "An empty schedule means the last pull had no trips. A desk without a Nanode sync token cannot send them.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.47",
    summary: "Pilot screens sit in a bottom bar",
    bullets: [
      "Home, Brief, Duty, Log, Pax, Checklist, and More stay in a fixed bottom bar on the phone and the tablet.",
      "About, Night, Refresh, and sign-out live on More, so the header no longer wraps.",
      "A screen with no assignment says the desk has not assigned a flight.",
      "A landscape tablet still shows Brief and Log side by side. Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.46",
    summary: "About matches the bundled shell",
    bullets: [
      "About says the flight screens open the pages stored in the app. Account and board calls still use the Pages proxy.",
      "Check for updates leaves a note when this phone cannot read its own version, instead of staying blank.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.45",
    summary: "Android sign-in reaches Nanode",
    bullets: [
      "The Android app sends account, sync, and weather calls to the Pages proxy. It does not post them at the files inside the APK.",
      "A sign-in reply that is not JSON says the server returned an unexpected page.",
      "The Pages proxy and the sync server allow the Capacitor origin. The browser app still uses same-origin /api.",
      "The splash still leaves on the first frame, or after a few seconds. Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.44",
    summary: "Pilot Android shell",
    bullets: [
      "The same companion installs as an Android app. The Pages site still deploys on its own. Removing the Android project leaves the web app as it is.",
      "Home opens on today's next leg, with a week strip and a side menu for the account, base, and saved log drafts.",
      "On a phone, Brief, Duty, Log, Pax, and Checklist sit in a bottom bar with icons. A landscape tablet shows Brief and Log side by side.",
      "The log sets off-block, takeoff, landing, and on-block, checks the order, and keeps a draft on the phone if the app closes. Open squawks sit on the brief.",
      "The Android app opens the pages bundled in the APK. The splash leaves on the first frame, or after a few seconds, and does not wait on the network. A newer shell still installs from the update bar.",
      "A newer Android shell shows a bar in the chrome. It downloads from the public Pages manifest, checks the file, then hands it to the installer. The flight screens stay up.",
      "Phone and tablet layouts use the shared desk tokens, including Ops Classic day and night. Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.43",
    summary: "The phone reads the aircraft database",
    bullets: [
      "The company snapshot now carries the aircraft database. The phone reads that list and does not keep a second copy.",
      "H25B fuel, passengers, and range on the published tails are the same figures the desk saved.",
      "A missing database stays empty. The phone does not invent a type.",
    ],
  },
  {
    version: "0.1.42",
    summary: "Pilot logon opens on Ops Classic Day",
    bullets: [
      "The phone opens on the pilot logon in Ops Classic Day. Night uses the same tokens. A saved desk theme still wins after sign-in.",
      "Phone tabs are Trip, Load, Weather, Map, Log, and Dashboard. A tablet in landscape uses the top bar and a docked edit panel.",
      "On a phone the edit-panel sections are swipe pages. Edit trip opens as a pull-up sheet.",
      "Fleet and trips are read from the desk board. A missing fleet stays empty. These pages are empty until the EFB content phase.",
      "Controls use the shared theme tokens: flat fields, checks, and scrollbars. No second color set.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.41",
    summary: "A fresh phone opens on Ops Classic",
    bullets: [
      "With no saved chrome, the phone opens on Ops Classic in night mode and the Northstar blue accent. Reset chrome returns to that same default.",
      "A saved desk theme still wins. Original, Vista, XP, Glassy, and Ops Night stay selectable.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.40",
    summary: "Schedule and crew read the live fleet",
    bullets: [
      "Sign-in, the schedule lanes, and the crew snapshot use the fleet on the company snapshot. A missing list stays empty.",
      "A tail added on the desk appears on the phone with that registration and type. A removed tail is not left behind as a lane.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.39",
    summary: "Trip weather brief with plain METAR/TAF and chart images",
    bullets: [
      "Brief for the selected trip leads with plain-language METAR and the first TAF period. Raw text stays behind Show raw.",
      "Radar, echo tops, and satellite IR frame the route from the same MSC and IEM products as the desk map. The WPC day-1 prog is included when it loads.",
      "A chart that fails shows an unavailable note. Nothing is invented.",
      "The company line is the joined company on the snapshot. No carrier name is hard-coded.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.38",
    summary: "Installable home-screen app and a one-lane day strip",
    bullets: [
      "Chrome and Edge show Install when the browser can add the app. It opens standalone. Icons are the navy jet mark at 180, 192, and 512.",
      "iPhone has no install prompt. The banner says Share, then Add to Home Screen. Safari is the reliable path. Already installed, or Not now, stays quiet for this session.",
      "Schedule leads with a one-lane day strip of the same pulled tickets. Tap a block to open that trip. The fleet timeline below is unchanged.",
      "The service worker caches the app shell network-first. /api and weather are not cached, so a board pull stays live.",
      "Independent of the desk number. No Community Detect, scan wipe, or Megan / Twilio change.",
    ],
  },
  {
    version: "0.1.37",
    summary: "Flight map dots and the altitude trail match the desk chart",
    bullets: [
      "Airport dots on Dark and Satellite use the lighter VFR green, MVFR blue, IFR red, and LIFR magenta. No category yet stays a light neutral, not a black dot. The dots are smaller.",
      "Every field drawn on the map is in the weather refresh, about every 30 minutes. A METAR that resolves paints one of the four flight-rule colors.",
      "The flown path is thinner. Altitude color blends along the line instead of one flat band between samples.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.36",
    summary: "The phone acks a schedule revision only after the board arrives",
    bullets: [
      "A new company revision does not count as received until /v1/state returns the snapshot and that snapshot’s schedule hash matches the ping.",
      "A failed pull keeps the previous board and does not ack the ping. If the hash does not match, More says so and the phone Pulls again.",
      "Sign-in still shows this account’s career archive only when Nanode has no company snapshot. That archive does not ack a company revision.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.35",
    summary: "Flight map shows the filed plan, not a thick dashed line",
    bullets: [
      "When this ticket has OFP waypoint positions, the map draws those fixes and the legs between them. The thick dashed line is not drawn on top.",
      "With no stored plan, the origin-to-dest hint is a thin dashed great-circle. It does not dominate the chart.",
      "The altitude trail stays the flown path. Enroute names stay small.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.34",
    summary: "After sign-in, the phone board and night theme follow the desk",
    bullets: [
      "Sign-in pulls the Nanode company board again and does not treat that pull as done until the snapshot arrives. A miss stays on More and retries. Live position still updates on its own channel.",
      "If the signed-in name does not match a crew row, Schedule shows the company CEL#### / F=ferry tickets instead of an empty list. More says to pick your name. Live SimLink status still paints.",
      "Night or day follows the desk snapshot (deskChrome), not this phone’s system appearance. A later same-company career save can correct a board snapshot that is still on Day. The revision ping no longer repaints Day every second.",
      "When Nanode has no company snapshot yet, More says the phone is showing this account’s career archive. A career for a different company does not replace the Nanode board.",
      "Independent of the desk number. No Megan / Twilio / SIP / voice change.",
    ],
  },
  {
    version: "0.1.33",
    summary: "Phone chrome uses the same spacing as the desk themes",
    bullets: [
      "Cards, fields, header, and Schedule tabs follow the shared spacing, radius, and 1px border. Original night surfaces and the other packs still change color only.",
      "Buttons and fields use the shared control height so labels are not clipped on day or night.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.32",
    summary: "Thicker distance line, same plane as the live map",
    bullets: [
      "The flight-status distance line is thicker, matching the desk. Covered versus remaining stays its own line.",
      "The plane on that bar is the live-map own-ship mark, signal amber, scaled for the bar, nose along the rail. It is not a second drawing.",
      "Phase labels still only highlight the current state. The phone does not gain a second ticket list.",
      "Independent of the desk number. No Megan / Twilio / SIP / voice change.",
    ],
  },
  {
    version: "0.1.31",
    summary: "Schedule shows the desk ticket, including a manual flight",
    bullets: [
      "A new CEL#### or F=ferry on the desk stays on this phone after the Nanode schedule nudge. A missed board pull is retried. It is not marked done before the snapshot arrives, and it is not polled on the live 1s tick.",
      "Live plane status still updates on the fast channel. That update no longer replaces the board that just arrived.",
      "If today’s timeline is empty because Local and Zulu disagree on the day, Schedule opens the nearest day that has the ticket. Bars keep a readable width instead of collapsing.",
      "Dispatcher view is still the company board. A pilot still sees only assigned trips. Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.29",
    summary: "Trip cargo is extra freight, not passenger bags",
    bullets: [
      "Trip edit labels cargo as extra freight, beyond the bags already included with each passenger. It is the same ticket field the desk sends to SimBrief.",
      "About 80 lb per person (5 passengers and 400 lb of bags) is not extra. Heavier cargo, golf, and medical still count. The phone does not build the flight plan.",
      "Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.28",
    summary: "Live map trail matches the desk — altitude color, follow, same path",
    bullets: [
      "The Flight map draws the same persisted ticket trail the desk keeps on Nanode. Opening the phone no longer starts the line over from the current fix. A new sample is pulled with the live channel; the full path is fetched only when that trail changes.",
      "The trail is colored by the same Northstar altitude bands as the desk, with a short legend. Missing altitude stays slate.",
      "Follow plane keeps the aircraft centered at the zoom you set. Pan or zoom pauses follow until you turn it back on. The map does not jump back to a fixed zoom.",
      "The plane is the larger signal-amber mark, not a theme color, so it stays visible on light, dark, and satellite.",
      "Installed scenery dots match the desk for this crew. The phone prefetches METAR and TAF for the 32 nearest fields in view, about once a minute. Tap or hover outside that batch fetches that field. A miss says no METAR / TAF — nothing is invented.",
      "Those dots use the same larger halo and FAA category colors as the desk: VFR green, MVFR blue, IFR red, LIFR magenta. No category yet stays a light neutral. A short Wx legend sits on the map.",
      "When this ticket already has OFP waypoint lat/lon, the ahead line is that whole plan, drawn solid. With no stored plan it stays a dashed great-circle. The altitude trail stays the flown path.",
      "Independent of the desk number. No Megan / Twilio / SIP / voice change.",
    ],
  },
  {
    version: "0.1.27",
    summary: "Schedule Gantt zoom for short legs",
    bullets: [
      "Schedule has a zoom slider. Higher zoom stretches the swipe axis so a short CEL#### or F=ferry pill is wide enough to read.",
      "The phone opens at the desk time scale from the company snapshot. Moving the slider here is for this visit. Local|Z stays the clock.",
      "Lane names stay pinned and swipe still pans. Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.26",
    summary: "Schedule Local follows the airport timezone",
    bullets: [
      "Schedule Local uses the same airport timezone as the desk. The phone has no live surface sample, so Local is the active trip’s origin, otherwise the parked field, otherwise the company base.",
      "Port Oxbury stays ADT in summer and AST in winter. A Pacific or Mountain origin shifts the Local caption and the bar times. Z stays Zulu.",
      "An ICAO with no timezone on file shows Zulu in the caption. It does not pretend to be ADT.",
      "Trip-card STD labels stay the Atlantic customer clock. Independent of the desk number. No Megan / Twilio / SIP change.",
    ],
  },
  {
    version: "0.1.25",
    summary: "Schedule Gantt fits a phone",
    bullets: [
      "Schedule opens a fleet / crew timeline of the same pulled Nanode tickets as the list — CEL#### and F=ferry, not a second board.",
      "On a phone or Fold cover the timeline scrolls sideways. Tail and crew names stay pinned. Bars stay wide enough to read a flight number.",
      "Tap a bar for the number, route, and times, then Open brief. Day / week and Local / Z follow the desk clock until you change them here.",
      "Empty tails stay off the phone. A grounded tail still shows AOG. Sticky bottom nav does not cover the controls.",
      "Independent of the desk number. No Megan / Twilio / SIP / voice change.",
    ],
  },
  {
    version: "0.1.24",
    summary: "Current flight follows desk Mark Active — no inverted pair",
    bullets: [
      "Nanode company activeFlightId (desk / Megan Mark Active) is the phone’s current trip, even with no live kinematics.",
      "Activate shows Active / Armed, not Airborne. Wheels-off still comes from live sim only. Last parking beats a Narnia ground spawn.",
      "Same shared binder as the desk: human bind first, then live jury, schedule last. Reverse city-pairs are not invented.",
      "Independent of the desk number. No Megan / Twilio / SIP change on the phone.",
    ],
  },
  {
    version: "0.1.23",
    summary: "Home-screen icon refresh — navy jet + radar",
    bullets: [
      "Add to Home Screen / PWA icons (192 and 512) are Emerson’s navy rounded-square mark: white twin-engine jet, cyan radar rings, NE chevron.",
      "Favicon SVG matches the same art. Manifest and apple-touch paths stay put.",
      "Independent of the desk number. No Megan / Twilio / SIP / Nanode change.",
    ],
  },
  {
    version: "0.1.22",
    summary: "CFPS NOTAMs on the phone; CheckWX does not unlock them",
    bullets: [
      "Public CFPS JSON (Canada-capable) is tried before AviationAPI. Same ops-impact filter as the desk.",
      "Desk-published last-known on Nanode still wins when the public lists miss.",
      "Copy no longer says a CheckWX key unlocks NOTAMs. Optional FAA keys stay US-only on the Windows desk.",
      "Independent of the desk number. No Megan / Twilio / SIP.",
    ],
  },
  {
    version: "0.1.21",
    summary: "Desk-matched own-ship + bold contrast routes",
    bullets: [
      "Own-ship is the same north-up silhouette and 20.8px size as the desk (~40% of the large-icon era). Centered on lat/lon. Hover / tap shows C-GVXL. No always-on callsign box.",
      "Dashed filed route and published track are ~3–4× heavier with a contrasting halo on Light, Dark, and Satellite so they do not wash out on a night basemap.",
      "Dep / dest / divert pins keep a theme fill plus a light or dark outline so airport marks stay visible on every basemap.",
      "Between Nanode kinematics pulls the plane dead-reckons on last GS + track (or last segment) and eases onto the next fix. Missing speed/track holds last pos.",
      "Independent of the desk number. No Megan / Twilio / SIP.",
    ],
  },
  {
    version: "0.1.20",
    summary: "Flight map stays put while the info list scrolls",
    bullets: [
      "On Flight, the Mapbox viewport is pinned. Scrolling the ops / trip column no longer drags the map off screen.",
      "Cover / narrow phone keeps the map stacked on top and only the list below scrolls. Unfolded Fold / tablet keeps map | info, with the map stationary and the right column scrolling.",
      "Header and sticky bottom nav stay on screen. Touch pan / zoom on the map surface is unchanged.",
      "No Megan / Twilio / SIP / voice change. Independent of the desk number.",
    ],
  },
  {
    version: "0.1.19",
    summary: "My trips default + Dispatcher crew reassign",
    bullets: [
      "Pilot phone default is My trips only (assigned CEL#### + linked ferries). Company schedule is read-only and only when Dispatch allows it and this crew opted in. Inbox stays off this phone.",
      "A Dispatcher account can reassign a live ticket to another company pilot — same roster gate as the desk, separate from Edit STD. Missing Content.xml airports need accept on transfer.",
      "Reassigned tickets show on the target’s My trips after they accept. Independent of the desk number.",
    ],
  },
  {
    version: "0.1.18",
    summary: "Sticky map basemap + NOTAMs tab + Content.xml schedule gate",
    bullets: [
      "Satellite / Light / Dark stick in localStorage — leaving Flight and coming back does not reset the basemap. Overlay chrome is grouped (Basemap / Flight / FIR). FIR stays off until you ask; CFPS radar stays on the desk because this phone’s wx proxy only allowlists NOAA METAR / TAF.",
      "Own-ship is the map center when a published fix exists. Hover shows an abbreviated tail / flight number — no always-on callsign plate. Plane mark stays phone-sized.",
      "Dedicated NOTAMs tab lists the same ops-impact runway / taxiway / airport / lighting / navaid items as Brief. Tap a scheduled trip for the weather brief; open NOTAMs from Brief, Flight, or the new tab.",
      "When the desk’s “Schedule only Content.xml airports” setting is on, dest / origin writes outside the scanned Community list are refused on this phone. Cancel and STD / load edits do not change the city-pair, so they stay allowed. Independent of the desk number.",
    ],
  },
{
    version: "0.1.17",
    summary: "Map overlays: airports, dashed route, live METAR/NOTAM, FIR",
    bullets: [
      "Origin/dest marks are stronger. Divert-to is amber; intended dest stays visible but dimmed. Filed route is a dashed great-circle when the snapshot has both ends.",
      "Tap an airport for live METAR / short TAF (public NOAA via the phone proxy) and critical NOTAMs. Refresh on open and about every 60s. No invented D-ATIS.",
      "FIR / UIR toggle + current-FIR chip use the same VAT-Spy boundary file as the desk. Hover a FIR for its name. VATSIM ATC waits.",
      "Own-aircraft popup shows published alt / GS / heading / phase when the snapshot has them. Missing token or wx still fails soft. Independent of the desk number.",
    ],
  },
{
    version: "0.1.16",
    summary: "Mapbox GL flight tracker from the desk-published token",
    bullets: [
      "Flight map uses Mapbox GL JS instead of OpenStreetMap raster tiles — same Nanode-published aircraft, heading, origin/dest/divert, and track as before. The plane is the same top-down airliner silhouette as the phase bar.",
      "The public pk token and optional day/night style URLs come from the desk Dispatcher Settings snapshot on Pull. Map style follows the desk theme (light ↔ dark). Satellite is a Flight tracker toolbar toggle. No token on this phone until the desk Pushes one.",
      "Missing token fails soft: Recenter and Satellite stay, tiles stay empty, nothing is invented. Recenter still fits the published route including a divert field.",
      "Pinned Home / Schedule / Flight / Brief / More chrome is unchanged. No Megan / Twilio / SIP / voice changes. Independent of the desk number.",
    ],
  },
  {
    version: "0.1.15",
    summary: "Richer trips + Dispatcher cancel / STD on the Nanode board",
    bullets: [
      "Trip cards show desk status colors, diversion line when published, and clearer STD / STA / ETE / load. Weather brief and NOTAM hooks stay on Brief.",
      "A Dispatcher (or dispatcher+pilot) account can cancel a flight — same reason dropdown as the desk — and edit STD / STA / pax / cargo. Writes POST /v1/board/ticket on the company snapshot; the next Pull is the Sim desk source of truth.",
      "Pilot stays assigned-only and cannot cancel or retime. Role is the roster on this company, not a second company on the phone.",
      "Wide / Fold layout uses a stronger schedule | live split, 4-up trip facts, and clearer padding. Sticky bottom nav is unchanged.",
      "No Megan / Twilio / SIP / voice change. Independent of the desk number.",
    ],
  },
  {
    version: "0.1.14",
    summary: "Current / delayed / on time from the same desk ticket",
    bullets: [
      "Flight and Schedule now key off the pulled Mission ticket — same CEL number, ops STD, and live lifecycle the desk tracker and Flight plans use.",
      "Current is the in-progress / next ticket by ops STD. Later legs sit below. Completed and cancelled drop. No SI route guess.",
      "A desk or Megan retime on Nanode shows on the next pull. Inbox RFQ clocks are not a second schedule.",
      "No Megan / Twilio / SIP / voice change. Independent of the desk number.",
    ],
  },
  {
    version: "0.1.13",
    summary: "On time / Delayed / Early / Cancelled on Flight and Schedule",
    bullets: [
      "Schedule cards and the Flight phase bar show the same derived ops status as the desk: On time, Delayed, Early, or Cancelled.",
      "Cancelled still wins. Compare live/actuals to the current pulled ticket STD/STA after a desk or Megan retime — never an older RFQ. Delayed if still on the stand past that STD. Early if departure or arrival beats the locked times.",
      "Home next-trip and Live glance use the same labels. Status is computed from the pulled ticket plus published kinematics — nothing is persisted on the phone.",
      "No Megan / Twilio / SIP / voice changes. Independent of the desk number.",
    ],
  },
  {
    version: "0.1.12",
    summary: "Desk-synced ops NOTAMs + readable weather cards",
    bullets: [
      "NOTAMs were empty because the phone only called public AviationAPI (that host often fails DNS) and the desk never published its live brief. Mobile now reads payload.opsNotamBrief from the Nanode snapshot — the same filtered runway/taxiway/airport/lighting/navaid list the desk already fetched.",
      "If AviationAPI answers, that live list still wins. If it does not, last-known from the desk is shown with an honest timestamp. Empty or error when neither source has flight-affecting items — nothing is invented.",
      "Weather brief is dep / enroute / dest cards in plain language (wind, vis, weather, ceiling). Raw METAR/TAF stays behind Show raw. Flight strip uses the same one-line decode.",
      "Pinned Home / Schedule / Flight / Brief / More chrome is unchanged. No Megan / Twilio / SIP / voice changes.",
    ],
  },
  {
    version: "0.1.11",
    summary: "Pinned app chrome + trip weather / NOTAM brief",
    bullets: [
      "Header and bottom nav stay on screen. Long pages scroll in the middle pane only — no more hunting for the bar after a scroll.",
      "Five-tab Android-style shell: Home / Schedule / Flight / Brief / More, with icons + labels and safe-area padding for cover, unfolded Fold, and home indicator.",
      "Tap a scheduled or next trip to open Brief: weather (dep/dest METAR/TAF) and flight-affecting NOTAMs (closed runways/taxiways, airport closed, lighting/navaid outages).",
      "Live NOAA + public AviationAPI when reachable (same family as the desk). Mission-stamped originMetar/destMetar show if the desk already wrote them. Empty states when nothing came back — nothing is invented.",
      "Flight tab stays the live tracker (map + phase rail). On cover width the tracker grows with the page so the map is not clipped; unfolded Fold still uses the map | ops split. A compact strip links to Brief so the same brief is not copied three times. Refresh settings and SimLink kinematics are unchanged.",
      "Nanode still has no live weatherFields / notamBrief payload. Those field names are listed on Brief for a later desk publish. No Megan / Twilio / voice changes.",
    ],
  },
  {
    version: "0.1.10",
    summary: "Phase bar matches desk: smooth cruise + Ground-in",
    bullets: [
      "Flight rail uses the same dispatch-core progress as the desk: remaining NM through climb–cruise–descent so the plane keeps moving.",
      "After landing, Ground (taxi-in / parked) stays at the end — the line does not jump back to Out.",
      "Current-step diamond no longer stacks under the designed plane silhouette (desk #89 parity).",
      "Phase labels stay Out / Taxi / TO / Clb / Crz / Des / App / Land / Gnd. Live kinematics still come from the published SimConnect sample — this PR does not invent a second live channel.",
    ],
  },
  {
    version: "0.1.9",
    summary: "Flight tab pulls company SimLink live even when crew presence misses",
    bullets: [
      "Live kinematics pull `/v1/live` about once a second whenever a board snapshot exists — including when `/v1/revision` is down.",
      "Company SimConnect / SI lastKnown draws on Flight / glance even if crew presence does not match this phone’s focus (multi-crew or post-wipe ids).",
      "A stale last-known persist still does not leak as another pilot’s fix on a multi-crew board.",
      "Board full-pull stays on the revision nudge. Theme and schedule are unchanged.",
      "Redeploy Cloudflare Pages (`northstar-companion`) so phones pick up this build. SYNC_TOKEN / NANODE_ORIGIN stay as they are.",
      "User-visible name stays Northstar Dispatch Mobile. No Megan / Twilio / voice changes.",
    ],
  },
  {
    version: "0.1.8",
    summary: "Flight tracker map + desk-style phase bar",
    bullets: [
      "Flight tab uses the same OpenStreetMap tile family as the desk — not a blank glance.",
      "Company aircraft is a heading-aware plane icon (not a circle). Origin / dest dots and a published track draw when Nanode has them.",
      "Desk-style phase/progress rail with the plane: smooth left-to-right flow, Ground after landing. Idle until a live SimConnect sample is published.",
      "Ops fields match what SimLink / Nanode already send: pilot, type, tail, route, phase, alt ft, GS kt, HDG/TRK, squawk, ETE, ETA. No invented kinematics.",
      "Cover / phone stacks a compact map + 2-column strip. Unfolded Fold and tablet use map | ops + trip. Recenter refits without chasing every live tick.",
      "Home still shows a live glance. User-visible name stays Northstar Dispatch Mobile. No Megan / Twilio / voice changes.",
    ],
  },
  {
    version: "0.1.7",
    summary: "CF 1003 — prefer HTTPS Nanode host for Pages",
    bullets: [
      "Hosted /api no longer fetches a bare IPv4 Nanode origin (Cloudflare error 1003 / HTTP 403).",
      "Pages proxy default is https://voice.northstardispatch.ca — the same HTTPS Nanode host Desk Sync already uses.",
      "A Pages env NANODE_ORIGIN that is a raw http(s) IPv4 address falls back to that hostname. Named hosts stay as set.",
      "No phone UI, voice, or desk-sync behavior change.",
    ],
  },
  {
    version: "0.1.6",
    summary: "Cloudflare Pages wrangler config",
    bullets: [
      "Pages deploy no longer fails on an unsupported `observability` block in wrangler.jsonc.",
      "Companion wrangler config keeps name, Pages output dir, compatibility date, and NANODE_ORIGIN only.",
      "No phone UI or sync-behavior change — this is a hosting-config fix so Cloudflare Pages can publish again.",
    ],
  },
  {
    version: "0.1.5",
    summary: "Per-account login — no shared mash-up",
    bullets: [
      "Open prompts the same Nanode email + password as the Windows desk. Home / Schedule / Flight stay locked until then.",
      "This phone shows only the signed-in account’s crew trips. Sign-out / switch account clears the cached board before a friend signs in.",
      "Cached snapshot is tagged to the account id so two logins on one phone cannot mix boards.",
      "Joined-company name and shared board come from the Nanode snapshot. Leave or switch on the desk; this phone updates on the next nudge.",
      "Theme pack and accent follow the desk on the next board nudge. No phone theme picker.",
      "Crew focus auto-matches the signed-in name when it can. Pick your name if it does not.",
    ],
  },
  {
    version: "0.1.4",
    summary: "Two channels: live ~1s, board on nudge",
    bullets: [
      "Live flight (position, speed, altitude, track) pulls about once a second via `/v1/live` — not the full board.",
      "Schedule / assignments / company chrome full-pull only when `/v1/revision` says the board changed.",
      "Idle board = almost no snapshot traffic. More still chooses On-screen vs Constant for those pings.",
      "15s / 30s (or 5s on-screen) snapshot runs only if the revision ping is down.",
    ],
  },
  {
    version: "0.1.3",
    summary: "Freshness over Nanode conservation",
    bullets: [
      "On-screen still pulls a full snapshot every 5s while visible. Constant uses the 15s / 30s choice.",
      "Those intervals are not stretched to spare the Nanode — small crew, prefer freshness.",
      "Live track stays ~1s. Manual Pull snapshot is unchanged.",
      "Phones may still throttle background timers in Constant mode. On-screen is the reliable path.",
    ],
  },
  {
    version: "0.1.2",
    summary: "Sign-in gate, desk theme, live vs schedule refresh",
    bullets: [
      "Open prompts the same Nanode account as the desk. Sign-out clears this phone’s cached board.",
      "Schedule and company chrome update when the desk publishes a change. Live track pulls about once a second.",
      "More: Only when on screen (default) or Constant updating. Phones may throttle background timers.",
      "Theme follows the desk for this account. No separate phone theme picker.",
      "Joined-company membership and this crew’s trips come from the signed-in account’s Nanode snapshot.",
    ],
  },
  {
    version: "0.1.1",
    summary: "Phone hosting + version lock",
    bullets: [
      "User-visible name is Northstar Dispatch Mobile (home screen, title, About). Package path stays apps/companion.",
      "Cloudflare Pages (`northstar-companion.pages.dev`) serves the PWA over HTTPS.",
      "Same-origin `/api` Function proxies to the HTTP Nanode so the phone never hits mixed content.",
      "SYNC_TOKEN stays a Pages secret. Sign in with the Nanode account, then Pull snapshot.",
      "This app’s version is its own product number, independent of the desk. Header always shows vX.Y.Z.",
      "More → Release Notes lists what’s new per build. CI requires a bump when the mobile app changes.",
    ],
  },
  {
    version: "0.1.0",
    summary: "First phone build",
    bullets: [
      "Phone-first PWA: Home / Schedule / Flight / More. Read-only crew snapshot.",
      "Pulls the same Nanode `GET /v1/state` the desk Pushes. Never PUTs the board.",
      "Crew focus, theme tokens, and cached last pull on this phone.",
      "Does not generate RFQs, write Inbox, or replace PSTN Megan.",
    ],
  },
];

export const RELEASE_NOTES_SEEN_KEY = "ns.companion.releaseNotes.seenVersion";

export function notesNewestFirst(
  notes: readonly CompanionReleaseNote[] = RELEASE_NOTES,
): CompanionReleaseNote[] {
  return [...notes].sort((a, b) => compareNoteVersion(b.version, a.version));
}

export function notesForVersion(
  version: string,
  notes: readonly CompanionReleaseNote[] = RELEASE_NOTES,
): CompanionReleaseNote | undefined {
  return notes.find((entry) => entry.version === version);
}

export function currentReleaseNotes(
  version = APP_VERSION,
  notes: readonly CompanionReleaseNote[] = RELEASE_NOTES,
): CompanionReleaseNote | undefined {
  return notesForVersion(version, notes);
}

export function readSeenReleaseVersion(storage: Pick<Storage, "getItem"> | null | undefined): string | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(RELEASE_NOTES_SEEN_KEY);
    return raw && /^\d+\.\d+\.\d+$/.test(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeSeenReleaseVersion(
  version: string,
  storage: Pick<Storage, "setItem"> | null | undefined,
): void {
  if (!storage) return;
  try {
    storage.setItem(RELEASE_NOTES_SEEN_KEY, version);
  } catch {
    /* quota / private mode */
  }
}

export function isNewSinceLastOpen(
  entryVersion: string,
  seenVersion: string | null,
  currentVersion = APP_VERSION,
): boolean {
  if (entryVersion !== currentVersion) return false;
  if (!seenVersion) return true;
  return compareNoteVersion(currentVersion, seenVersion) > 0;
}

function compareNoteVersion(a: string, b: string): number {
  const left = a.split(".").map(Number);
  const right = b.split(".").map(Number);
  for (let i = 0; i < 3; i += 1) {
    const delta = (left[i] ?? 0) - (right[i] ?? 0);
    if (delta) return delta;
  }
  return 0;
}
